import { axiosInstance } from 'app/utils/axiosConfig'
import {
  SponsorRequest,
  SponsorRequestComplete,
  SponsorRequestStatus,
} from 'EstraSponsor'

import { endpoints } from './endpoints'

export type GetAllSponsorRequestsResponse = SponsorRequest[]

export const getAllSponsorRequests =
  async (): Promise<GetAllSponsorRequestsResponse | null> => {
    return axiosInstance
      .get(endpoints.sponsorRequest.getAllSponsorRequests)
      .then(({ data }) => data)
  }

export type GetSponsorRequestParams = {
  sponsor_request_id: string
}

export type GetSponsorRequestResponse = SponsorRequest

export const getSponsorRequest = async (
  data: GetSponsorRequestParams
): Promise<GetSponsorRequestResponse | null> => {
  return axiosInstance
    .get(
      endpoints.sponsorRequest.getSponsorRequest.makeUrl(
        data.sponsor_request_id
      )
    )
    .then(({ data }) => data)
}

export type SetStatusSponsorRequestParams = {
  sponsor_request_id: string
}

export type SetStatusSponsorRequestPayload = {
  status: Extract<
    SponsorRequestStatus,
    'new' | 'canceled' | 'approved' | 'finalized'
  >
}

export type SetStatusSponsorRequestResponse = SponsorRequest

export const setStatusSponsorRequest = async (
  params: SetStatusSponsorRequestParams,
  data: SetStatusSponsorRequestPayload
): Promise<SetStatusSponsorRequestResponse> => {
  return axiosInstance
    .put(
      endpoints.sponsorRequest.setStatusSponsorRequest.makeUrl(
        params.sponsor_request_id
      ),
      data
    )
    .then(({ data }) => data)
}

type SponsorRequestPayload = NullableOptional<
  Pick<
    SponsorRequestComplete,
    | 'scope'
    | 'timeline'
    | 'brand_name'
    | 'purpose'
    | 'place'
    | 'target_audience'
    | 'logo_visibility'
    | 'financial_contribution'
    | 'previous_edition_attendees'
    | 'nr_events_for_season'
    | 'attendees_average_for_event'
    | 'website_link'
    | 'facebook_link'
    | 'instagram_link'
    | 'twitter_link'
    | 'youtube_link'
    | 'tiktok_link'
    | 'financial_contribution'
    | 'technical_contribution'
    | 'benefits'
    | 'press_office_name'
    | 'press_office_email'
    | 'press_office_phone_number'
    | 'commercial_referent_name'
    | 'commercial_referent_email'
    | 'commercial_referent_phone_number'
    | 'ethic_code_accepted'
    | 'anti_corruption_policy_accepted'
  >
> &
  Pick<SponsorRequest, 'name'> & {
    region_id: Maybe<string>
    province_id: Maybe<string>
    city_id: Maybe<string>
    sponsor_request_events_attributes: {
      start_on: Maybe<string>
      end_on: Maybe<string>
      attendees: Maybe<string>
    }[]
  }

export type CreateSponsorRequestPayload = SponsorRequestPayload
export type CreateSponsorRequestResponse = SponsorRequest

export const createSponsorRequest = async (
  data: CreateSponsorRequestPayload
): Promise<CreateSponsorRequestResponse> => {
  return axiosInstance
    .post(endpoints.sponsorRequest.createSponsorRequest, data)
    .then(({ data }) => data)
}

export type UpdateSponsorRequestParams = {
  sponsor_request_id: string
}

export type UpdateSponsorRequestPayload = SponsorRequestPayload

export type UpdateSponsorRequestResponse = SponsorRequest

export const updateSponsorRequest = async (
  params: UpdateSponsorRequestParams,
  data: UpdateSponsorRequestPayload
): Promise<UpdateSponsorRequestResponse> => {
  return axiosInstance
    .put(
      endpoints.sponsorRequest.updateSponsorRequest.makeUrl(
        params.sponsor_request_id
      ),
      data
    )
    .then(({ data }) => data)
}

export type UploadSponsorRequestFilesParams = {
  sponsor_request_id: string
}

export type UploadSponsorRequestFilesResponse = SponsorRequest

export const uploadSponsorRequestFiles = async (
  params: UploadSponsorRequestFilesParams,
  data: FormData
): Promise<UploadSponsorRequestFilesResponse> => {
  return axiosInstance
    .put(
      endpoints.sponsorRequest.uploadSponsorRequestFiles.makeUrl(
        params.sponsor_request_id
      ),
      data
    )
    .then(({ data }) => data)
}

export type UploadSignedOutcomeDocumentParams = {
  sponsor_request_id: string
}

export type UploadSignedOutcomeDocumentResponse = SponsorRequest

export const uploadSignedOutcomeDocument = async (
  params: UploadSignedOutcomeDocumentParams,
  data: FormData
): Promise<UploadSignedOutcomeDocumentResponse> => {
  return axiosInstance
    .put(
      endpoints.sponsorRequest.uploadSignedOutcomeDocument.makeUrl(
        params.sponsor_request_id
      ),
      data
    )
    .then(({ data }) => data)
}

export type DeleteSponsorRequestParams = {
  sponsor_request_id: string
}

export const deleteSponsorRequest = async (
  data: GetSponsorRequestParams
): Promise<void> => {
  return axiosInstance.delete(
    endpoints.sponsorRequest.deleteSponsorRequest.makeUrl(
      data.sponsor_request_id
    )
  )
}

export type DeleteSponsorshipProofsParams = {
  sponsor_request_id: number
  sponsorship_proof_id: number
}

export const deleteSponsorshipProofs = async (
  params: DeleteSponsorshipProofsParams
): Promise<void> => {
  return axiosInstance.delete(
    endpoints.sponsorRequest.deleteSponsorshipProofs.makeUrl(
      params.sponsor_request_id,
      params.sponsorship_proof_id
    )
  )
}
